import React, {useState, useEffect } from 'react';
import {useParams, useNavigate, useLocation } from 'react-router-dom';

// Import Font Awesome components and icons
import {FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrashAlt, // Using faTrashAlt for trash can icon
  faUpload,
  faPlusCircle,
  faSave,
  faArrowLeft,
  faTimesCircle, // For the remove button on media previews
} from '@fortawesome/free-solid-svg-icons';

// Firebase Imports
import {getAuth } from 'firebase/auth';
import {doc, getDoc, updateDoc, Timestamp, collection, query, where, getDocs } from 'firebase/firestore';
import {ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {db, storage } from '../../firebase';

// Styled components imports (ensure these are defined in your listings.style.ts)
import {
  ListingsContainer,
  DashboardHeader,
  DashboardTitle,
  ContentSection,
  FormGroup,
  Label,
  Input,
  TextArea,
  Select,
  Button,
  MediaUploadContainer,
  MediaPreview,
  LoadingMessage,
  ErrorMessage,
  FormSection,
  ContactInputsContainer,
} from '../../pages/registeredUsers/styles/listings.style';

// Categories are now determined by the stall's category, not hardcoded

const mediaTypes = [
  {value: '', label: 'Select Media Type' },
  {value: 'item_image', label: 'Item Image (Description, Price, Image)' },
  {value: 'video', label: 'Video (Description, 1-minute Video)' },
  {value: 'general_image', label: 'General Image (Description, Image)' },
];

declare const __app_id: string | undefined;

interface MediaItemBase {
  id: string;
  url: string;
  description?: string;
  isNew?: boolean;
  originalUrl?: string;
  file?: File;
}

interface ImageMediaItem extends MediaItemBase {
  type: 'image';
  price?: number | string;
}

interface VideoMediaItem extends MediaItemBase {
  type: 'video';
}

interface ListingMedia {
  url: string;
  description: string;
  price?: number;
  type: 'image' | 'video';
}

interface Listing {
  id: string;
  listingId: string;
  title: string;
  description: string;
  category: string;
  contact: string;
  sold: boolean;
  images: string[];
  videos?: string[];
  mediaDetails?: ListingMedia[];
  userId: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  price?: number;
}

const EditListingPage = () => {
  const {id: listingId } = useParams<{id: string }>();
  const navigate = useNavigate();
  const auth = getAuth();
  const userId = auth.currentUser?.uid;
  
  // Get stall number from URL params or localStorage
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const stallNumberFromUrl = queryParams.get('stallNumber');
  const stallNumberFromStorage = localStorage.getItem('currentStallNumber');
  const stallNumber = stallNumberFromUrl || stallNumberFromStorage;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    contactEmail: '',
    contactPhone: '',
  });

  const [stallCategory, setStallCategory] = useState<string>('');

  const [selectedMediaType, setSelectedMediaType] = useState('');
  const [itemImageDescription, setItemImageDescription] = useState('');
  const [itemImagePrice, setItemImagePrice] = useState('');
  const [itemImageFile, setItemImageFile] = useState<File | null>(null);
  const [videoDescription, setVideoDescription] = useState('');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [generalImageDescription, setGeneralImageDescription] = useState('');
  const [generalImageFile, setGeneralImageFile] = useState<File | null>(null);

  const [currentImages, setCurrentImages] = useState<ImageMediaItem[]>([]);
  const [currentVideos, setCurrentVideos] = useState<VideoMediaItem[]>([]);

  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
  const [videosToDelete, setVideosToDelete] = useState<string[]>([]);

  const [stallType, setStallType] = useState<'Premium' | 'Regular' | null>(null);

  // Determine limits based on stall type
  const isPremium = stallType === 'Premium';
  const maxImages = isPremium ? 15 : 10;
  const maxVideos = isPremium ? 5 : 2;

  useEffect(() => {
    if (!userId || !listingId || !stallNumber) {
      setError("Authentication, Listing ID, or Stall Number missing.");
      setLoading(false);
      return;
    }

    const fetchListing = async () => {
      try {
        const listingDocRef = doc(db, `stalls/${stallNumber}/listings`, listingId);
        const docSnap = await getDoc(listingDocRef);

        if (docSnap.exists()) {
          const data = docSnap.data() as Listing;
          setFormData({
            title: data.title,
            description: data.description,
            category: data.category,
            contactEmail: data.contact.split(' / ')[0] || '',
            contactPhone: data.contact.split(' / ')[1] || '',
          });

          // Fetch stall details to get the stall's category
          try {
            const stallDocRef = doc(db, 'stalls', stallNumber);
            const stallSnap = await getDoc(stallDocRef);
            if (stallSnap.exists()) {
              const stallData = stallSnap.data();
              setStallCategory(stallData.category || data.category);
              setStallType(stallData.stallType || 'Regular');
            } else {
              setStallCategory(data.category);
              setStallType('Regular');
            }
          } catch (stallError) {
            console.warn("Could not fetch stall details:", stallError);
            setStallCategory(data.category);
            setStallType('Regular');
          }

          // Handle mediaDetails structure
          const existingImages: ImageMediaItem[] = [];
          const existingVideos: VideoMediaItem[] = [];

          if (data.mediaDetails && data.mediaDetails.length > 0) {
            data.mediaDetails.forEach((media: ListingMedia) => {
              if (media.type === 'image') {
                existingImages.push({
                  id: media.url,
                  url: media.url,
                  type: 'image',
                  description: media.description || '',
                  price: media.price || data.price,
                  isNew: false,
                  originalUrl: media.url,
                });
              } else if (media.type === 'video') {
                existingVideos.push({
                  id: media.url,
                  url: media.url,
                  type: 'video',
                  description: media.description || '',
                  isNew: false,
                  originalUrl: media.url,
                });
              }
            });
          } else {
            // Fallback to old structure if mediaDetails doesn't exist
            if (data.images) {
              data.images.forEach(url => {
                existingImages.push({
                  id: url,
                  url: url,
                  type: 'image',
                  description: '',
                  price: data.price,
                  isNew: false,
                  originalUrl: url,
                });
              });
            }
            if (data.videos) {
              data.videos.forEach(url => {
                existingVideos.push({
                  id: url,
                  url: url,
                  type: 'video',
                  description: '',
                  isNew: false,
                  originalUrl: url,
                });
              });
            }
          }

          setCurrentImages(existingImages);
          setCurrentVideos(existingVideos);

        } else {
          setError("Listing not found.");
        }
      } catch (err) {
        console.error("Error fetching listing:", err);
        setError("Failed to load listing for editing.");
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [userId, listingId, stallNumber]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {name, value } = e.target;
    setFormData(prev => ({...prev, [name]: value }));
  };

  const handleMediaTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMediaType(e.target.value);
    setItemImageDescription('');
    setItemImagePrice('');
    setItemImageFile(null);
    setVideoDescription('');
    setVideoFile(null);
    setGeneralImageDescription('');
    setGeneralImageFile(null);
  };

  const handleMediaFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'item_image' | 'video' | 'general_image') => {
    const file = e.target.files ? e.target.files[0] : null;

    if (!file) return;

    if (type === 'video') {
      if (!file.type.startsWith('video/')) {
        alert("Please upload a video file.");
        e.target.value = '';
        return;
      }
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = function() {
        URL.revokeObjectURL(video.src);
        if (video.duration > 60) {
          alert("Video must be 1 minute or less.");
          e.target.value = '';
          setVideoFile(null);
        } else {
          setVideoFile(file);
        }
      };
      video.src = URL.createObjectURL(file);
    } else {
      if (!file.type.startsWith('image/')) {
        alert("Please upload an image file.");
        e.target.value = '';
        return;
      }
      if (type === 'item_image') {
        setItemImageFile(file);
      } else if (type === 'general_image') {
        setGeneralImageFile(file);
      }
    }
  };

  const addNewMediaToPreview = () => {
    if (selectedMediaType === 'item_image') {
      if (!itemImageFile || !itemImageDescription || !itemImagePrice) {
        alert("Please provide description, price, and select an image for Item Image.");
        return;
      }
      if (currentImages.length >= maxImages) {
        alert(`Maximum ${maxImages} images reached. Remove some to add more.`);
        return;
      }
      setCurrentImages(prev => [
        ...prev,
        {
          id: `new-img-${Date.now()}`,
          url: URL.createObjectURL(itemImageFile),
          file: itemImageFile,
          type: 'image',
          description: itemImageDescription,
          price: Number(itemImagePrice),
          isNew: true,
        }
      ]);
      setItemImageDescription('');
      setItemImagePrice('');
      setItemImageFile(null);
    } else if (selectedMediaType === 'video') {
      if (!videoFile || !videoDescription) {
        alert("Please provide description and select a video.");
        return;
      }
      if (currentVideos.length >= maxVideos) {
        alert(`Maximum ${maxVideos} videos reached. Remove some to add more.`);
        return;
      }
      setCurrentVideos(prev => [
        ...prev,
        {
          id: `new-vid-${Date.now()}`,
          url: URL.createObjectURL(videoFile),
          file: videoFile,
          type: 'video',
          description: videoDescription,
          isNew: true,
        }
      ]);
      setVideoDescription('');
      setVideoFile(null);
    } else if (selectedMediaType === 'general_image') {
      if (!generalImageFile || !generalImageDescription) {
        alert("Please provide description and select an image for General Image.");
        return;
      }
      if (currentImages.length >= maxImages) {
        alert(`Maximum ${maxImages} images reached. Remove some to add more.`);
        return;
      }
      setCurrentImages(prev => [
        ...prev,
        {
          id: `new-gen_img-${Date.now()}`,
          url: URL.createObjectURL(generalImageFile),
          file: generalImageFile,
          type: 'image',
          description: generalImageDescription,
          isNew: true,
        }
      ]);
      setGeneralImageDescription('');
      setGeneralImageFile(null);
    } else {
      alert("Please select a media type first.");
    }
    setSelectedMediaType('');
  };

  const handleRemoveMedia = (idToRemove: string, type: 'image' | 'video', originalUrl?: string) => {
    if (type === 'image') {
      setCurrentImages(prev => {
        const itemToRemove = prev.find(item => item.id === idToRemove);
        if (itemToRemove && itemToRemove.isNew) {
          URL.revokeObjectURL(itemToRemove.url);
        } else if (originalUrl) {
          setImagesToDelete(prevUrls => [...prevUrls, originalUrl]);
        }
        return prev.filter(item => item.id !== idToRemove);
      });
    } else {
      setCurrentVideos(prev => {
        const itemToRemove = prev.find(item => item.id === idToRemove);
        if (itemToRemove && itemToRemove.isNew) {
          URL.revokeObjectURL(itemToRemove.url);
        } else if (originalUrl) {
          setVideosToDelete(prevUrls => [...prevUrls, originalUrl]);
        }
        return prev.filter(item => item.id !== idToRemove);
      });
    }
  };

  const uploadFileToFirebaseStorage = async (file: File, stallNumber: string, listingId: string, isVideo: boolean): Promise<string> => {
    const fileExtension = file.name.split('.').pop();
    const filePath = `stalls/${stallNumber}/listings/${listingId}/${isVideo ? 'videos' : 'images'}/${file.name}_${Date.now()}.${fileExtension}`;
    const fileRef = ref(storage, filePath);
    await uploadBytes(fileRef, file);
    return getDownloadURL(fileRef);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !listingId || !stallNumber) {
      alert("User not authenticated, Listing ID missing, or Stall Number missing.");
      return;
    }
    if (isSubmitting) return;

    setIsSubmitting(true);

    const combinedContact = `${formData.contactEmail || ''}${formData.contactEmail && formData.contactPhone ? ' / ' : ''}${formData.contactPhone || ''}`;
    if (!combinedContact) {
      alert("Please provide at least an email or a phone number for contact.");
      setIsSubmitting(false);
      return;
    }

    if (currentImages.filter(img => !img.isNew).length + currentVideos.filter(vid => !vid.isNew).length === 0 &&
        currentImages.filter(img => img.isNew).length + currentVideos.filter(vid => vid.isNew).length === 0) {
      alert("Please ensure your listing has at least one existing or new image/video.");
      setIsSubmitting(false);
      return;
    }

    try {
      const listingDocRef = doc(db, `stalls/${stallNumber}/listings`, listingId);

      for (const url of imagesToDelete) {
        try {
          await deleteObject(ref(storage, url));
          console.log(`Deleted old image: ${url}`);
        } catch (storageError: any) {
          if (storageError.code !== 'storage/object-not-found') {
            console.warn(`Could not delete image ${url}: ${storageError.message}`);
          }
        }
      }
      for (const url of videosToDelete) {
        try {
          await deleteObject(ref(storage, url));
          console.log(`Deleted old video: ${url}`);
        } catch (storageError: any) {
          if (storageError.code !== 'storage/object-not-found') {
            console.warn(`Could not delete video ${url}: ${storageError.message}`);
          }
        }
      }

      const newImageUrls: string[] = [];
      const newVideoUrls: string[] = [];

      for (const media of currentImages.filter(m => m.isNew && m.file)) {
        const url = await uploadFileToFirebaseStorage(media.file!, stallNumber, listingId, false);
        newImageUrls.push(url);
      }
      for (const media of currentVideos.filter(m => m.isNew && m.file)) {
        const url = await uploadFileToFirebaseStorage(media.file!, stallNumber, listingId, true);
        newVideoUrls.push(url);
      }

      const finalImageUrls = currentImages
        .filter(m => !m.isNew && !imagesToDelete.includes(m.originalUrl || ''))
        .map(m => m.url)
        .concat(newImageUrls);

      const finalVideoUrls = currentVideos
        .filter(m => !m.isNew && !videosToDelete.includes(m.originalUrl || ''))
        .map(m => m.url)
        .concat(newVideoUrls);

      // Create mediaDetails array
      const mediaDetails: ListingMedia[] = [];
      
      // Add existing images that weren't deleted
      currentImages
        .filter(m => !m.isNew && !imagesToDelete.includes(m.originalUrl || ''))
        .forEach(img => {
          mediaDetails.push({
            url: img.url,
            type: 'image',
            description: img.description || '',
            price: typeof img.price === 'number' ? img.price : undefined,
          });
        });

      // Add new images with their URLs
      currentImages
        .filter(m => m.isNew)
        .forEach((img, index) => {
          mediaDetails.push({
            url: newImageUrls[index] || img.url,
            type: 'image',
            description: img.description || '',
            price: typeof img.price === 'number' ? img.price : undefined,
          });
        });

      // Add existing videos that weren't deleted
      currentVideos
        .filter(m => !m.isNew && !videosToDelete.includes(m.originalUrl || ''))
        .forEach(vid => {
          mediaDetails.push({
            url: vid.url,
            type: 'video',
            description: vid.description || '',
          });
        });

      // Add new videos with their URLs
      let newVideoIndex = 0;
      currentVideos
        .filter(m => m.isNew)
        .forEach(vid => {
          mediaDetails.push({
            url: newVideoUrls[newVideoIndex] || vid.url,
            type: 'video',
            description: vid.description || '',
          });
          newVideoIndex++;
        });

      const mainListingPrice = currentImages.find(img => typeof img.price !== 'undefined' && img.price !== '')?.price;

      const updatedListingData: Partial<Listing> = {
        title: formData.title,
        description: formData.description,
        category: stallCategory || formData.category, // Use stall's category
        contact: combinedContact,
        images: finalImageUrls,
        videos: finalVideoUrls,
        mediaDetails: mediaDetails,
        updatedAt: Timestamp.now(),
        price: typeof mainListingPrice === 'number' ? mainListingPrice : undefined,
      };

      await updateDoc(listingDocRef, updatedListingData);

      alert("Listing updated successfully!");
      navigate('-1');

    } catch (err) {
      console.error("Error updating listing:", err);
      setError("Failed to update listing. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };


  if (loading) {
    return <LoadingMessage>Loading listing details...</LoadingMessage>;
  }

  if (error) {
    return <ErrorMessage>{error} <Button onClick={() => navigate(-1)}><FontAwesomeIcon icon={faArrowLeft} /> Back to Listings</Button></ErrorMessage>;
  }

  if (!userId) {
    return <ErrorMessage>You must be logged in to edit listings.</ErrorMessage>;
  }

  return (
    <ListingsContainer>
      <DashboardHeader>
        <DashboardTitle>Edit Listing</DashboardTitle>
        <Button onClick={() => navigate(-1)} variant="outline">
          <FontAwesomeIcon icon={faArrowLeft} style={{marginRight: '8px' }} /> Back to My Listings
        </Button>
      </DashboardHeader>

      <ContentSection>
        <form onSubmit={handleUpdate}>
          <FormSection>
            <h3>Basic Listing Information</h3>
            <FormGroup>
              <Label>Category</Label>
              <div style={{
                padding: '10px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                color: '#495057'
              }}>
                <strong>{stallCategory || formData.category}</strong>
                <p style={{margin: '5px 0 0 0', fontSize: '0.8em', color: '#6c757d'}}>
                  Category is determined by your stall registration and cannot be changed here.
                </p>
              </div>
            </FormGroup>

            <FormGroup>
              <Label>Title</Label>
              <Input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Samsung Galaxy S23 Ultra"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Description</Label>
              <TextArea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Provide a detailed description of your item..."
                rows={4}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Contact Information</Label>
              <ContactInputsContainer>
                <Input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  placeholder="Email (e.g., example@email.com)"
                />
                <Input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  placeholder="Phone (e.g., 07XXXXXXXX)"
                />
              </ContactInputsContainer>
              {(!formData.contactEmail && !formData.contactPhone) && (
                <p style={{color: 'red', fontSize: '0.8em', marginTop: '0.5rem' }}>
                  Please provide at least an email or a phone number.
                </p>
              )}
            </FormGroup>
          </FormSection>

          <FormSection>
            <h3>Manage Media (Images & Videos)</h3>
            <h4>Current Media:</h4>
            <MediaUploadContainer style={{marginBottom: '20px', minHeight: '100px', border: '1px solid #ddd', padding: '10px', borderRadius: '8px' }}>
              {(currentImages.length > 0 || currentVideos.length > 0) ? (
                <>
                  {currentImages.map((media) => (
                    <MediaPreview key={media.id}>
                      <img src={media.url} alt={`Preview of ${media.description}`} />
                      <div className="media-info">
                        <p>{media.description || 'Image'}</p>
                        {media.price && <p>Ugx: {Number(media.price).toLocaleString()}</p>}
                        {media.isNew && <span style={{fontSize: '0.7em', color: 'green'}}>(New)</span>}
                      </div>
                      <button type="button" onClick={() => handleRemoveMedia(media.id, 'image', media.originalUrl)}>
                        <FontAwesomeIcon icon={faTimesCircle} />
                      </button>
                    </MediaPreview>
                  ))}
                  {currentVideos.map((media) => (
                    <MediaPreview key={media.id}>
                      <video src={media.url} controls />
                      <div className="media-info">
                        <p>{media.description || 'Video'}</p>
                        {media.isNew && <span style={{fontSize: '0.7em', color: 'green'}}>(New)</span>}
                      </div>
                      <button type="button" onClick={() => handleRemoveMedia(media.id, 'video', media.originalUrl)}>
                        <FontAwesomeIcon icon={faTimesCircle} />
                      </button>
                    </MediaPreview>
                  ))}
                </>
              ) : (
                <p style={{textAlign: 'center', width: '100%', color: '#666' }}>
                  No media uploaded yet for this listing.
                </p>
              )}
            </MediaUploadContainer>
            <p style={{fontSize: '0.8em', color: '#888', marginTop: '10px' }}>
                Total Images: {currentImages.length}/{maxImages} | Total Videos: {currentVideos.length}/{maxVideos}
            </p>

            <h4>Add New Media:</h4>
            <FormGroup>
              <Label htmlFor="mediaType">Select Type of Media to Add</Label>
              <Select id="mediaType" value={selectedMediaType} onChange={handleMediaTypeChange}>
                {mediaTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </Select>
            </FormGroup>

            {selectedMediaType === 'item_image' && (
              <>
                <FormGroup>
                  <Label htmlFor="itemImageDescription">Item Description</Label>
                  <Input
                    type="text"
                    id="itemImageDescription"
                    value={itemImageDescription}
                    onChange={(e) => setItemImageDescription(e.target.value)}
                    placeholder="e.g., Brand new, 256GB"
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="itemImagePrice">Price (Ugx)</Label>
                  <Input
                    type="number"
                    id="itemImagePrice"
                    value={itemImagePrice}
                    onChange={(e) => setItemImagePrice(e.target.value)}
                    placeholder="e.g., 1500000"
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="itemImageFile">Upload Item Image</Label>
                  <Input
                    type="file"
                    id="itemImageFile"
                    accept="image/*"
                    onChange={(e) => handleMediaFileChange(e, 'item_image')}
                    key={itemImageFile ? itemImageFile.name : 'no-file-item'}
                  />
                  {itemImageFile && <p>Selected: {itemImageFile.name}</p>}
                </FormGroup>
                <Button type="button" onClick={addNewMediaToPreview} disabled={!itemImageFile || currentImages.length >= maxImages}>
                  <FontAwesomeIcon icon={faPlusCircle} style={{marginRight: '5px'}}/> Add Item Image
                </Button>
              </>
            )}

            {selectedMediaType === 'video' && (
              <>
                <FormGroup>
                  <Label htmlFor="videoDescription">Video Description</Label>
                  <Input
                    type="text"
                    id="videoDescription"
                    value={videoDescription}
                    onChange={(e) => setVideoDescription(e.target.value)}
                    placeholder="e.g., Features of the item"
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="videoFile">Upload Video (Max 1 minute)</Label>
                  <Input
                    type="file"
                    id="videoFile"
                    accept="video/mp4,video/webm,video/ogg"
                    onChange={(e) => handleMediaFileChange(e, 'video')}
                    key={videoFile ? videoFile.name : 'no-file-video'}
                  />
                  {videoFile && <p>Selected: {videoFile.name}</p>}
                </FormGroup>
                <Button type="button" onClick={addNewMediaToPreview} disabled={!videoFile || currentVideos.length >= maxVideos}>
                  <FontAwesomeIcon icon={faPlusCircle} style={{marginRight: '5px'}}/> Add Video
                </Button>
              </>
            )}

            {selectedMediaType === 'general_image' && (
              <>
                <FormGroup>
                  <Label htmlFor="generalImageDescription">Image Description</Label>
                  <Input
                    type="text"
                    id="generalImageDescription"
                    value={generalImageDescription}
                    onChange={(e) => setGeneralImageDescription(e.target.value)}
                    placeholder="e.g., Different angle, packaging"
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="generalImageFile">Upload General Image</Label>
                  <Input
                    type="file"
                    id="generalImageFile"
                    accept="image/*"
                    onChange={(e) => handleMediaFileChange(e, 'general_image')}
                    key={generalImageFile ? generalImageFile.name : 'no-file-general'}
                  />
                  {generalImageFile && <p>Selected: {generalImageFile.name}</p>}
                </FormGroup>
                <Button type="button" onClick={addNewMediaToPreview} disabled={!generalImageFile || currentImages.length >= maxImages}>
                  <FontAwesomeIcon icon={faPlusCircle} style={{marginRight: '5px'}}/> Add General Image
                </Button>
              </>
            )}
          </FormSection>

          <Button type="submit" disabled={isSubmitting} style={{marginTop: '20px'}}>
            <FontAwesomeIcon icon={faSave} style={{marginRight: '8px' }} /> {isSubmitting ? 'Updating Listing...' : 'Save Changes'}
          </Button>
        </form>
      </ContentSection>
    </ListingsContainer>
  );
};

export default EditListingPage;